import { Suspense, lazy, useEffect, useState } from 'react'
import { AlertCircle, Eye, Loader2, X } from 'lucide-react'
import {
  fetchControlCardSigner,
  previewJobControlCards,
  type PendingControlCard,
} from '@/services/jobControlCards.service'

const PdfPreview = lazy(() => import('@/components/ui/PdfPreview'))

const DEFAULT_CONTROLLED_AGAINST = 'Forskrift Nr 1357, 1360'

interface JobControlCardsPreviewProps {
  cards: PendingControlCard[]
  onGenerate: () => void
}

/** Watermarked preview of the pending cards with today's date; the publish dialog sets the real values. */
const JobControlCardsPreview = ({ cards, onGenerate }: JobControlCardsPreviewProps) => {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [failures, setFailures] = useState<{ id: string; label: string; error: string }[]>([])

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url)
  }, [url])

  const close = () => {
    setUrl(null)
    setFailures([])
  }

  const handlePreview = async () => {
    setFailures([])
    setProgress({ done: 0, total: cards.length })
    try {
      const performer = await fetchControlCardSigner()
      if (!performer) throw new Error('Brukerprofil mangler navn')
      const result = await previewJobControlCards({
        cards,
        performedDate: new Date().toISOString().split('T')[0],
        controlledAgainst: DEFAULT_CONTROLLED_AGAINST,
        performedBy: performer,
        onProgress: (done, total) => setProgress({ done, total }),
      })
      setFailures(
        result.failed.map(({ card, error }) => ({
          id: card.assignment.id,
          label: card.equipment.serial_number || card.equipment.asset_number || 'Ukjent utstyr',
          error,
        })),
      )
      if (result.blob) setUrl(URL.createObjectURL(result.blob))
      else alert('Forhåndsvisning feilet')
    } catch (err) {
      console.error('Control card preview failed:', err)
      alert(err instanceof Error ? err.message : 'Forhåndsvisning feilet')
    } finally {
      setProgress(null)
    }
  }

  return (
    <>
      <button
        onClick={handlePreview}
        disabled={progress !== null}
        className="h-11 px-4 rounded-md text-sm font-semibold text-[#1E3A5F] bg-white border border-slate-300 hover:bg-slate-100 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
      >
        {progress ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
        {progress ? `${Math.min(progress.done + 1, progress.total)} av ${progress.total}...` : 'Forhåndsvis'}
      </button>

      {url && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 shrink-0">
            <p className="flex-1 min-w-0 text-base font-semibold text-slate-800 truncate">
              Forhåndsvisning
              <span className="text-slate-300 mx-2">·</span>
              <span className="text-slate-400 font-normal">
                {cards.length} kontrollkort
              </span>
            </p>
            <button
              onClick={() => {
                close()
                onGenerate()
              }}
              className="h-11 px-4 rounded-md text-sm font-semibold text-white bg-[#1E3A5F] hover:bg-[#16304f]"
            >
              Generer kontrollkort
            </button>
            <button
              onClick={close}
              className="h-11 w-11 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100"
              aria-label="Lukk forhåndsvisning"
            >
              <X size={20} />
            </button>
          </div>
          {failures.length > 0 && (
            <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 space-y-1 shrink-0">
              {failures.map((f) => (
                <p key={f.id} className="flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  {f.label}: {f.error}
                </p>
              ))}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto bg-slate-100">
            <Suspense
              fallback={
                <div className="h-full flex items-center justify-center">
                  <Loader2 size={24} className="animate-spin text-slate-400" />
                </div>
              }
            >
              <PdfPreview src={url} title="Kontrollkort" />
            </Suspense>
          </div>
        </div>
      )}
    </>
  )
}

export default JobControlCardsPreview
